import React, { useEffect } from 'react';
import { Button, Table } from 'antd';
import { connect } from 'react-redux';
import { ColumnsType } from 'antd/es/table';
import { Link, useHistory, useLocation } from 'react-router-dom';
import FixStickyTable from '@/components/FixStickyTable';
import { EmptyHint } from '@/components/EmptyHint';
import { getCheckinHistoryPageList, saveData, setPagination } from '@/redux/actions/guest';
import { GuestInitialStateProps, IguestInfo } from '@/redux/reducers/guest';
import { DATE_TIME_FORMAT, MEMBER_TYPE_MAP } from '@/config';
import getRandomId from '@/utils/uuid';
import { renderIntlLabel, storage } from '@/utils';
import { getTimeByUtcOffset } from '@/utils/intl';

const mapDispatchToProps = {
  getCheckinHistoryPageListFn: getCheckinHistoryPageList,
  setPaginationFn: setPagination,
  saveDataFn: saveData,
};

type TCheckinHistory = {
  guestInfo: Partial<IguestInfo>;
  onClick: (...args) => void;
  onCloseDrawer: () => void;
} & GuestInitialStateProps &
  typeof mapDispatchToProps;

const CheckinHistory: React.FC<Partial<TCheckinHistory>> = ({
  guestInfo,
  onClick,
  onCloseDrawer,
  checkinHistory,
  getCheckinHistoryPageListFn,
  setPaginationFn,
  saveDataFn,
}) => {
  const location: any = useLocation();
  const { checkinPageList, pageNum, pageSize, total, checkinPageListLoading } = checkinHistory;

  const handleTableChange = (_pagination) => {
    const { wyuid } = guestInfo || {};
    setPaginationFn({
      checkinHistory: {
        pageNum: _pagination.current,
        pageSize: _pagination.pageSize,
        total: _pagination.total,
      },
    });
    getCheckinHistoryPageListFn({ wyuid });
  };

  useEffect(() => {
    const { wyuid } = guestInfo || {};
    if (wyuid) {
      getCheckinHistoryPageListFn({ wyuid });
    }

    return () => {
      saveDataFn({
        checkinHistory: {
          pageNum: 1,
          pageSize: 50,
          total: 0,
        },
      });
    };
  }, [guestInfo]);

  const columns: ColumnsType<any> = [
    {
      title: renderIntlLabel('order_detail_checkin_identity'),
      dataIndex: 'memberType',
      render: (val) => MEMBER_TYPE_MAP[val],
    },
    { title: renderIntlLabel('order_commonCode'), dataIndex: 'commonCode' },
    { title: renderIntlLabel('order_commonName'), dataIndex: 'commonName' },
    {
      title: renderIntlLabel('order_roomorder_checkin_time'),
      dataIndex: 'checkinTime',
      render: (val) => (val ? getTimeByUtcOffset(val, DATE_TIME_FORMAT) : '—'),
    },
    {
      title: renderIntlLabel('order_roomorder_leave_time'),
      dataIndex: 'checkoutTime',
      render: (val) => (val ? getTimeByUtcOffset(val, DATE_TIME_FORMAT) : '—'),
    },
    {
      title: renderIntlLabel('order_action'),
      render: (_, record) =>
        storage.get('commonCode') === record.commonCode ? (
          <Button
            type="link"
            onClick={onCloseDrawer} // hotfix: 2020-08-08 在/order/room-order页面中的宾客详情抽屉点击详情没有反应，实际上页面路由是有变化的，产品陈天鸿提出关闭宾客详情抽屉
          >
            <Link
              className="order-detail"
              to={{
                pathname: `/order/room-order/${record.resvNo}`,
                state: {
                  fromUrl: window.location.pathname,
                  orderTabKey: location.state?.orderTabKey,
                },
              }}
            >
              {renderIntlLabel('order_detail')}
            </Link>
          </Button>
        ) : null,
      fixed: 'right',
      align: 'right',
    },
  ];

  return Array.isArray(checkinPageList) && !!checkinPageList.length ? (
    <FixStickyTable>
      <Table
        rowKey={() => getRandomId()}
        columns={columns}
        dataSource={checkinPageList}
        pagination={{
          current: pageNum,
          pageSize,
          total,
        }}
        loading={checkinPageListLoading}
        onChange={handleTableChange}
        scroll={{ x: 'max-content' }}
        sticky
      />
    </FixStickyTable>
  ) : (
    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 200 }}>
      <EmptyHint hintText={renderIntlLabel('guest_cannot_query_data')} />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    ...state.guest,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(CheckinHistory));
